import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { personIcon } from '../../assets/icons';
import { FaEllipsisV, FaTrash } from 'react-icons/fa';
import { FaRobot, FaUser } from 'react-icons/fa';
import { api } from '../../api';
import styles from './OwnedTab.module.css';
import ConfirmationDialog from './ConfirmationDialog';
import { useDevice } from '../../context/DeviceContext';
import type { CharacterListResponse } from '../../api/types';

interface OwnedTabCharactersProps {
  characters: CharacterListResponse[];
  isLoading: boolean;
  onRefresh: () => Promise<void>;
}

function OwnedTabCharacters({ characters, isLoading, onRefresh }: OwnedTabCharactersProps): JSX.Element {
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [characterToDelete, setCharacterToDelete] = useState<CharacterListResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  
  // Add state for character filtering
  const [humanCharacters, setHumanCharacters] = useState<CharacterListResponse[]>([]);
  const [agentCharacters, setAgentCharacters] = useState<CharacterListResponse[]>([]);
  const [showAgentCreated, setShowAgentCreated] = useState<boolean>(false);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);
  
  // Add effect to fetch and log agent vs human created characters
  useEffect(() => {
    const fetchAndLogCharacters = async (): Promise<void> => {
      try {
        setIsFilterLoading(true);
        const humanCreatedCharacters = await api.characters.owned(false);
        const agentCreatedCharacters = await api.characters.owned(true);
        
        console.log('Human-created characters:', humanCreatedCharacters);
        console.log('Agent-created characters:', agentCreatedCharacters);
        
        // Log counts for easy reference
        console.log(`Human-created characters: ${humanCreatedCharacters.length}`);
        console.log(`Agent-created characters: ${agentCreatedCharacters.length}`);
        
        // Store the characters in state
        setHumanCharacters(humanCreatedCharacters);
        setAgentCharacters(agentCreatedCharacters);
        setIsFilterLoading(false);
      } catch (error) {
        console.error('Error fetching character data:', error);
        setIsFilterLoading(false);
      }
    };
    
    // Only run this effect if we have characters (meaning the component is mounted and ready)
    if (characters && characters.length > 0) {
      fetchAndLogCharacters();
    }
  }, [characters]);

  // Always show human-created characters on mobile
  useEffect(() => {
    if (isMobile && showAgentCreated) {
      setShowAgentCreated(false);
    }
  }, [isMobile]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const handleCreateClick = (): void => {
    navigate('/create-character');
  };

  const toggleMenu = (e: React.MouseEvent, id: string): void => {
    e.stopPropagation();
    setMenuOpen(menuOpen === id ? null : id);
  };

  const openDeleteDialog = (e: React.MouseEvent, character: CharacterListResponse): void => {
    e.stopPropagation();
    setMenuOpen(null);
    setCharacterToDelete(character);
    setDialogOpen(true);
  };

  const handleDeleteCharacter = async (): Promise<void> => {
    if (!characterToDelete) return;
    
    setIsDeleting(true);
    
    try {
      // Get the character instance and delete it
      const character = await api.characters.get(characterToDelete.username);
      await character.delete();
      
      // Refresh the characters list
      onRefresh();
      setDialogOpen(false);
      setCharacterToDelete(null);
    } catch (error) {
      console.error('Failed to delete character:', error);
      setIsDeleting(false);
      // Could add a toast notification here for error feedback
    }
  };

  // Toggle between human and agent created characters
  const toggleCharacterType = (): void => {
    // Don't allow toggling on mobile devices
    if (isMobile) return;
    
    setShowAgentCreated(!showAgentCreated);
  };

  // Determine which characters to display based on toggle state
  const displayedCharacters = showAgentCreated ? agentCharacters : humanCharacters;

  if (isLoading || isFilterLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.tabContent}>
      {!isMobile && (
        <div className={styles.toggleContainer}>
          <button 
            className={`${styles.toggleButton} ${!showAgentCreated ? styles.activeToggle : ''}`}
            onClick={toggleCharacterType}
            aria-label="Show human-created characters"
          >
            <FaUser size={20} />
          </button>
          <button 
            className={`${styles.toggleButton} ${showAgentCreated ? styles.activeToggle : ''}`}
            onClick={toggleCharacterType}
            aria-label="Show agent-created characters"
          >
            <FaRobot size={20} />
          </button>
        </div>
      )}

      {displayedCharacters.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No {showAgentCreated ? 'agent' : 'human'}-created characters yet</p>
          {!showAgentCreated && (
            <button 
              className={styles.createButton}
              onClick={handleCreateClick}
            >
              Create Character
            </button>
          )}
        </div>
      ) : (
        <div className={styles.scrollContainer}>
          <div className={styles.listContent}>
            {displayedCharacters.map((character) => (
              <div 
                key={character.id} 
                className={styles.listItem}
                onClick={() => navigate(`/chars/${character.username}`)}
              >
                <div className={styles.itemImage}>
                  <img src={character.icon || personIcon} alt={character.display_name} />
                </div>
                <div className={styles.itemContent}>
                  <div className={styles.itemHeader}>
                    <h3>{character.display_name}</h3>
                    <div 
                      className={styles.menuContainer} 
                      onClick={(e) => e.stopPropagation()}
                      ref={menuOpen === `character-${character.id}` ? menuRef : null}
                    >
                      <button 
                        className={styles.menuButton} 
                        onClick={(e) => toggleMenu(e, `character-${character.id}`)}
                        aria-label="Character options"
                      >
                        <FaEllipsisV />
                      </button>
                      {menuOpen === `character-${character.id}` && (
                        <div className={styles.menuDropdown}>
                          <button 
                            className={styles.menuItem}
                            onClick={(e) => openDeleteDialog(e, character)}
                          >
                            <FaTrash className={styles.menuIcon} />
                            <span>Delete character</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className={styles.tagline}>{character.tagline}</p>
                  <span className={styles.username}>@{character.username}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setCharacterToDelete(null);
        }}
        onConfirm={handleDeleteCharacter}
        title="Delete Character"
        message={`Are you sure you want to delete ${characterToDelete?.display_name || 'this character'}? This action cannot be undone.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
}

export default OwnedTabCharacters; 